import React, { useState, useEffect, useCallback } from "react";

import { Box, Typography, Paper, Grid, Card, CardContent, Stack } from "@mui/material";

import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, CartesianGrid, Cell, Label} from "recharts";

import {ClipboardPenLine, Hospital, Slice, Users, Bed, Calendar } from "lucide-react";
import axiosConfig from "../config";
import { formatNumber, transformAnalyticsData } from "../util";

import _ from "lodash";

/** Plan: 
 * 
 *  "BarChart" could be used. 
 *       - Average waiting time number in the middle in the top 
 *       - How many patients in the hospital number on the top right next to the avg wait time
 *       - Average priority score on the topright
 * 
 *        Underneath: 
 *       - Pie chart for operation type distribution on the right
 *       - Pie chart for gender disribuition ojnn the left 
 *       - Waiting time distribution chart underneath. 
 * 
 *  It's good for future work, so it's more for extensibility and making the sidebar look nice. 
 */ 


// NHS Colour Palette (nice and clinical)
const nhsColours = [
  "#005EB8", // Primary Blue
  "#00A9CE", // Cyan
  "#007F3B", // Green
  "#ED8B00", // Orange
  "#A32E2E", // Dark Red
  "#6A1B9A", // Purple
  "#AE2573", //pink
];

export default function HospitalAnalytics() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [metrics, setMetrics] = useState({});
  const [upcoming, setUpcoming] = useState(0);
  const [hduBeds, setHduBeds] = useState(0);

  const [waitingDist, setWaitingDist] = useState([]);
  const [sexDist, setSexDist] = useState([]);
  const [opDist, setOpDist] = useState([]);

  // fetches metrics from our endpoint
  const fetchMetrics = useCallback(async () => {
    
    // endpoint
    let response = await axiosConfig.get("/operations/analytics/metrics", {
      params: {
        fields: "waitingMonths,priorityScore"
      }
    })

    setMetrics(response.data.metrics);
    

  }, [setMetrics, setLoading]);


  const fetchUpcoming = useCallback(async () => {
    
    // get date bounds
    let today = new Date();
    let oneMonthAway = new Date(today);
    oneMonthAway.setMonth(today.getMonth() + 1);

    let filter = JSON.stringify({
      theatreDate: {
        $gte: today.toISOString(),
        $lt: oneMonthAway.toISOString(),
      }
    })

    const res = await axiosConfig.get("/operations/analytics/metrics", {
      params: {
        filter: filter
      }
    })

    setUpcoming(res.data.metrics.count);
    

  }, [setUpcoming, setLoading]);

  const fetchHduBeds = useCallback(async () => {
    

    let filter = JSON.stringify({
      bedRequired: true
    })

    const res = await axiosConfig.get("/operations/analytics/metrics", {
      params: {
        filter: filter
      }
    })

    // console.log(res.data)

    setHduBeds(res.data.metrics.count);
    

  }, [setHduBeds, setLoading]);

  const fetchDistributions = useCallback(async () => {
    

    const waitRes = await axiosConfig.get("/operations/analytics", {
      params: {
        field: "waitingMonths"
      }
    })

    setWaitingDist(
      transformAnalyticsData(
        waitRes.data.analytics
      )
    );

    const sexRes = await axiosConfig.get("/operations/analytics", {
      params: {
        field: "sex"
      }
    })

    setSexDist(
      transformAnalyticsData(
        sexRes.data.analytics
      )
    );

    const opRes = await axiosConfig.get("/operations/analytics", {
      params: {
        field: "operationType"
      }
    })
    
    setOpDist(
      transformAnalyticsData(
        opRes.data.analytics
      )
    );

    

  }, [setWaitingDist, setSexDist, setOpDist, setLoading]);

  // actually make the queries
  useEffect(() => {
    setLoading(true);
    fetchMetrics();
    fetchDistributions();
    fetchUpcoming();
    fetchHduBeds();
    setLoading(false);
  }, [fetchMetrics, fetchDistributions, fetchUpcoming, fetchHduBeds, setLoading]);


  // 📈📈📈 summary metrics (mega stonks)
  const summaryMetrics = [
    {
      title: "Total Operations",
      icon: Users,
      value: formatNumber(metrics?.count ?? 0),
      colour: "#005EB8",
    },
    {
      title: "Average Wait Time",
      icon: Calendar,
      value: formatNumber(metrics?.waitingMonths ?? 0),
      unit: "months",
      colour: "#ED8B00",
    },
    {
      title: "Average Priority Score",
      icon: ClipboardPenLine,
      value: formatNumber(metrics?.priorityScore ?? 0),
      colour: "#007F3B",
    },
    {
      title: "Most Common Operation",
      icon: Hospital,
      value: opDist || null ? (
        _.maxBy(opDist, v => v.count)?.name === "0" ?
        "Hip Replacement" : "Knee Replacement"
      ) : "N/A",
      colour: "#A32E2E",
    },
    {
      title: "Upcoming Ops (This Month)",
      icon: Slice,
      value: formatNumber(upcoming ?? 0),
      colour: "#00A9CE",
    },
    {
      title: "Total HDU Beds Required",
      icon: Bed,
      value: formatNumber(hduBeds ?? 0),
      colour: "#6A1B9A",
    },
  ];

  if (loading) {
    return (
      <div>It's still loading, chillllllll</div>
    )
  }

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        NHS Hospital Analytics
      </Typography>

      {/* Summary Metrics */}
      <Grid container spacing={3} mb={4}>
        {summaryMetrics.map((metric, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Card elevation={2}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      backgroundColor: `${metric.colour}20`,
                      borderRadius: "50%",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <metric.icon size={22} color={metric.colour} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={600}>
                      {metric.value}
                      {metric.unit && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ fontSize: "0.8rem", ml: 0.5 }}
                        >
                          {metric.unit}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {metric.title}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Distribution Charts */}
      <Grid container spacing={2} mb={4}>
        {/* Gender Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" textAlign="center" gutterBottom>
              Gender Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sexDist}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="count"
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {sexDist.map((_, i) => (
                    <Cell
                      key={`gender-${i}`}
                      fill={nhsColours[(i + 2) % nhsColours.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(val) => `${val} patients`} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Operation Type */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" textAlign="center" gutterBottom>
              Operation Type Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={opDist}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="count"
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {opDist.map((_, i) => (
                    <Cell
                      key={`op-${i}`}
                      fill={nhsColours[i % nhsColours.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(val) => `${val} patients`} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Wait Time Chart */}
      <Grid item xs={12}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Waiting Time Distribution
          </Typography>
          
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={waitingDist}
              margin={{ top: 20, right: 20, left: 60, bottom: 70 }} 
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="name"
                interval={0}
                tickFormatter={label => label.replace(/–|-/g, ' to ')}
                padding={{ left: 10, right: 10 }}      //  optional extra space on the ends
              >
                <Label
                  value="Months Waited"
                  position="insideBottom"
                  offset={-15}                         
                  style={{ textAnchor: 'middle' }}
                />
              </XAxis>

              <YAxis
                label={{
                  value: 'Number of Patients',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 0,
                  style: { textAnchor: 'middle' }
                }}
              />

              <Tooltip
                formatter={(value) => [value, 'Number of Patients']}
                separator=": "
              />

              <Bar
                dataKey="count"
                fill="#ED8B00"
                radius={[4, 4, 0, 0]}
              >
                {waitingDist.map((_, i) => (
                  <Cell
                    key={`bar-${i}`}
                    fill={`hsl(${220 + i * 15}, 60%, ${70 - i * 5}%)`}
                  />
                ))}
              </Bar>
            </BarChart>
        </ResponsiveContainer>

        </Paper>
      </Grid>

    </Box>
  );
};
